<template>
  <v-container fluid class="pt">
    <form-dialog
      v-model="dialog"
      :title="customer.id ? $t('edit_record') : $t('new_record')"
    >
      <new-shareholder v-model="customer" @saved="save" />
    </form-dialog>
    <v-layout row wrap>
      <v-card class="elevation-0 shadow-md white mt-2 flex xs12">
        <v-card-text class="pt-3">
          <div class="di-flex flex align-center mb-3">
            <template v-if="can.add">
              <v-btn
                color="primary"
                @click="showDialog"
                v-if="$vuetify.breakpoint.mdAndUp"
              >
                <v-icon class="mx-2">add_circle</v-icon>
                <span class="mt-1">
                  {{ $t("new_record") }}
                </span>
              </v-btn>
              <v-btn
                icon
                flat
                v-else
                class="mx-0"
                color="primary"
                @click="showDialog"
              >
                <v-icon>add_circle</v-icon>
              </v-btn>
            </template>
            <v-spacer />

            <v-text-field
              outline
              hide-details
              v-model="search"
              style="max-width: 400px"
              class="flex xs12 rounded"
              :placeholder="$t('search')"
              prepend-inner-icon="search"
            />
            <v-btn
              color="grey darken-2"
              class="mx-0"
              flat
              icon
              @click="printTable(false)"
            >
              <v-icon>print</v-icon>
            </v-btn>
            <v-btn
              color="grey darken-2"
              class="mx-0"
              flat
              icon
              @click="printTable(true)"
            >
              <v-icon>file_download</v-icon>
            </v-btn>
            <v-btn
              flat
              icon
              class="mx-0"
              @click="getData"
              :loading="loading"
              color="grey darken-2"
            >
              <v-icon>refresh</v-icon>
            </v-btn>
          </div>
          <v-data-table
            ref="tbl"
            item-key="id"
            :items="items"
            class="flex xs12"
            :loading="loading"
            :headers="headers"
            v-model="selected"
            :total-items="meta.total"
            :pagination.sync="pagination"
            :rows-per-page-items="per_page"
          >
            <template slot="items" slot-scope="{ item, index }">
              <tr>
                <td>{{ index + 1 }}</td>
                <td>{{ item.id }}</td>
                <td>
                  <router-link
                    :to="`shareholders/${item.id}?type=shareholder`"
                    >{{ `${item.name} ` }}</router-link
                  >
                </td>
                <td>{{ item.father_name }}</td>
                <td>{{ item.pretty_phone }}</td>
                <td>{{ item.address }}</td>
                <td>{{ item.tazkira_no }}</td>
                <td>{{ item.nationality }}</td>
                <td>{{ item.place_of_birth }}</td>
                <td>{{ item.date_of_birth }}</td>
                <td>{{ item.salary }}</td>
                <td>{{ item.share_percentage }} %</td>
                <td v-if="can.any">
                  <v-btn
                    flat
                    icon
                    small
                    class="ma-0"
                    v-if="can.view"
                    color="grey darken-1"
                    @click="
                      $router.push(`shareholders/${item.id}?type=shareholder`)
                    "
                  >
                    <v-icon>launch</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    flat
                    class="ma-0"
                    v-if="can.edit"
                    color="grey darken-1"
                    @click="editItem(item, index)"
                  >
                    <v-icon>edit</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    flat
                    small
                    class="ma-0"
                    v-if="can.delete"
                    color="grey darken-1"
                    @click="deleteItem(item)"
                  >
                    <v-icon>delete_outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-layout>
  </v-container>
</template>

<script>
import debounce from "@/plugins/vDebounce";
import newShareholder from "./newShareholder.vue";
import { customersMixin } from "@/mixins/index.js";

export default {
  mixins: [customersMixin],
  components: { newShareholder },
  data() {
    return {
      items: [],
      search: "",
      selected: [],
      dialog: false,
      loading: false,
      meta: {
        total: 0,
        perPage: 0,
        current_page: 0,
      },
      customer: {},
      totalRows: 0,
      loading: true,
      editedIndex: -1,
      pagination: { page: 1, rowsPerPage: 50 },
    };
  },

  computed: {
    can() {
      return {
        add: this.userCan(`add_shareholders`),
        view: this.userCan(`view_shareholders`),
        edit: this.userCan(`edit_shareholders`),
        delete: this.userCan(`delete_shareholders`),
        any:
          this.userCan(`edit_shareholders`) ||
          this.userCan(`view_shareholders`) ||
          this.userCan(`delete_shareholders`),
      };
    },
    currencies() {
      return this.initialData.currencies;
    },
    headers: (vm) => {
      let items = [
        {
          value: "index",
          width: "100px",
          sortable: false,
          text: vm.$t("number"),
        },
        {
          value: "id",
          sortable: false,
          text: vm.$t("account_number"),
        },
        {
          value: "name",
          sortable: false,
          text: vm.$t("name"),
        },
        {
          value: "father_name",
          sortable: false,
          text: vm.$t("father_name"),
        },
        {
          sortable: false,
          value: "pretty_phone",
          text: vm.$t("phones"),
        },
        {
          sortable: false,
          value: "address",
          text: vm.$t("address"),
        },
        {
          sortable: false,
          value: "tazkira_no",
          text: vm.$t("tazkira_no"),
        },
        {
          sortable: false,
          value: "nationality",
          text: vm.$t("nationality"),
        },
        {
          sortable: false,
          value: "place_of_birth",
          text: vm.$t("place_of_birth"),
        },
        {
          sortable: false,
          value: "date_of_birth",
          text: vm.$t("date_of_birth"),
        },
        {
          sortable: false,
          value: "salary",
          text: vm.$t("share_amount"),
        },
        {
          sortable: false,
          value: "share_percentage",
          text: vm.$t("share_percentage"),
          computed:(item)=> `${item.share_percentage} %`
        },
      ];
      items.push({
        sortable: false,
        value: "actions",
        text: vm.$t("actions"),
      });
      return items;
    },
    // total() {
    //   let barrow = { name: "مجموع طلب" };
    //   let loan = { name: "مجموع قروض" };

    //   this.items.forEach(item => {
    //     this.currencies.forEach(currency => {
    //       if (
    //         item.mapped_accounts[currency.id] &&
    //         item.mapped_accounts[currency.id].balance < 0
    //       )
    //         loan[currency.id] =
    //           (loan[currency.id] || 0) +
    //           Number(item.mapped_accounts[currency.id].balance);
    //       else if (
    //         item.mapped_accounts[currency.id] &&
    //         item.mapped_accounts[currency.id].balance > 0
    //       )
    //         barrow[currency.id] =
    //           (barrow[currency.id] || 0) +
    //           Number(item.mapped_accounts[currency.id].balance);
    //     });
    //   });
    //   return { loan, barrow };
    // }
  },
  methods: {
    getData() {
      this.loading = true;
      let { search } = this;
      const { page, rowsPerPage } = this.pagination;
      let params = {
        page,
        search,
        type: "shareholder",
        per_page: rowsPerPage,
        is_bank: this.is_bank || false,
      };
      this.axios
        .get(`customers`, { params })
        .then((resp) => {
          this.items = resp.data.data;
          this.meta = resp.data.meta;
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
        });
    },

    editItem(item, index) {
      this.customer = { ...item };
      this.editedIndex = index;
      this.dialog = true;
    },

    deleteItem(item) {
      let vm = this;
      this.$alert("customers/" + item.id).then(() => {
        vm.items.splice(vm.items.indexOf(item), 1); // remove items
      });
    },

    reset() {
      this.dialog = false;
      this.customer = {
        name: "",
        phones: "",
        address: "",
        position_rank: 1,
        type: "shareholder",
        last_form_number: 100,
        is_bank: this.is_bank,
        position: "Shareholder",
      };
      this.editedIndex = -1;
    },

    showDialog() {
      this.reset();
      this.dialog = true;
    },

    save(customer) {
      if (this.editedIndex > -1) {
        Object.assign(this.items[this.editedIndex], customer);
      } else {
        this.items.push(customer);
      }
      this.reset();
    },

    printTable(excel) {
      let data = {
        landscape: true,
        title: this.$t(`shareholders`),
        headers: this.headers.slice(1, -1),
        items: this.$refs.tbl.filteredItems,
      };

      if (excel)
        return this.xl_tables(data, "shareholders", this.LONG_APP_NAME);
      this.$store.commit("ReportsModule/setTableData", data);
      this.$router.push("/print");
    },
  },
  created() {
    this.reset();
  },
  watch: {
    pagination: {
      handler() {
        this.getData();
      },
      deep: true,
    },

    search: debounce(function (newVal) {
      this.getData(newVal);
    }, 1000),
  },
};
</script>